import { formatDate } from '@angular/common';
import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSort } from '@angular/material/sort';
import { Router } from '@angular/router';
import { AutoUnsubscribe } from 'ngx-auto-unsubscribe';
import { Subscription } from 'rxjs';
import { TeamsService } from '../../admin360/student-info/teams/teams.service';
import { StudentList } from '../../classroom360/student-lists/student-list.model';
import { StudentListsService } from '../../classroom360/student-lists/student-lists.service';
import { StudentTeams } from '../../classroom360/student-teams/student-teams.model';
import { StaffRolesLookup } from '../../management360/staff/staff.model';
import { StaffService } from '../../management360/staff/staff.service';
import { TylerMatTableDataSource } from '../../shared-components/angular-material/extended.class';
import { ErrorList } from '../../shared-components/controls/error-prompt/error-list.model';
import { EnterCsvDialogComponent } from '../../shared-components/enter-csv-dialog/enter-csv-dialog.component';
import { StudentSearchCriteria } from '../../shared-components/student-search/student-search-criteria.model';
import { StudentService } from '../../shared-components/student/student.service';
import { Year } from '../../shared-components/year/year.model';
import { YearService } from '../../shared-components/year/year.service';
import { ScheduleHistoryModel, ScheduleHistorySearchModel } from './schedule-history.model';
import { ScheduleHistoryService } from './schedule-history.service';

@AutoUnsubscribe({ includeArrays: true })
@Component({
    selector: 'app-schedule-history',
    templateUrl: './schedule-history.component.html',
    styleUrls: ['./schedule-history.component.css']
})
export class ScheduleHistoryComponent implements OnInit, OnDestroy {
    defaultYearSubscription: Subscription;
    selectedStudentSubscription: Subscription;
    scheduleHistorySearchModel: ScheduleHistorySearchModel;
    scheduleHistoryModel: ScheduleHistoryModel[] = [];
    studentNumberRegExp = /^[^\s,]+(,[^\s,]+)*$/;
    studentNumberKeyRestriction = /[^\s,]|,/;
    subscriptions: Subscription[] = [];
    studentLists: StudentList[] = [];
    teams: StudentTeams[] = [];
    counselor: StaffRolesLookup[] = [];
    multipleStudent: boolean;
    private _studentList: StudentList = null;
    errors: ErrorList[] = [];
    defaultYear: Year;
    searchCriteria: StudentSearchCriteria;
    public schedule: ScheduleHistoryModel;
    angularMatData = new TylerMatTableDataSource();
    columnsToDisplay = ['COURSE', 'COURSECODE', 'SECTION', 'TEACHER', 'SCHOOLENTRY', 'SCHOOLEXIT', 'CLASSENTRY', 'CLASSEXIT', 'USERNAME', 'LASTUPDATEDDATE'];

    classEntryDateFrom: ElementRef;
    classEntryDateTo: ElementRef;
    classExitDateFrom: ElementRef;
    classExitDateTo: ElementRef;

    @ViewChild(MatSort, { static: true }) sort: MatSort;

    constructor(
        private router: Router,
        private scheduleHistoryService: ScheduleHistoryService,
        private dialog: MatDialog,
        private studentListsService: StudentListsService,
        private yearService: YearService,
        private teamsService: TeamsService,
        private counselorService: StaffService,
        private studentService: StudentService
    ) {
        this.multipleStudent = true;
        this.scheduleHistorySearchModel = new ScheduleHistorySearchModel('');

        this.defaultYear = this.yearService.defaultYear;
        this.selectedStudentSubscription = studentService.selectedStudentStream$.subscribe(studentId => {
            if (!this.multipleStudent) {
                this.scheduleHistorySearchModel = new ScheduleHistorySearchModel(this.studentService.selectedStudent.StudentNumber);
                this.getCourseChangeHistoryData();
            }
        });
    }

    get studentList(): StudentList {
        return this._studentList;
    }

    set studentList(value: StudentList) {
        this._studentList = value;
        if (this._studentList) {
            this.searchCriteria.listId = this._studentList.ID;
        }
    }

    ngOnInit(): void {
        this.multipleStudent = this.router.url.toLowerCase().indexOf('/schedulehistory/multiplestudent') > 0 ? true : false;
        if (this.multipleStudent) {
            this.defaultYearSubscription = this.yearService.observableDefaultYearStream$.subscribe(defaultYear => {
                this.defaultYear = defaultYear;
            });
            this.columnsToDisplay.unshift('STUDENTID');
            this.columnsToDisplay.unshift('STUDENTNAME');

            this.scheduleHistorySearchModel.classEntryDateFrom = formatDate(new Date(), 'MM/dd/yyyy', 'en-US');
            this.scheduleHistorySearchModel.classEntryDateTo = formatDate(new Date(), 'MM/dd/yyyy', 'en-US');
            this.scheduleHistorySearchModel.classExitDateFrom = formatDate(new Date(), 'MM/dd/yyyy', 'en-US');
            this.scheduleHistorySearchModel.classExitDateTo = formatDate(new Date(), 'MM/dd/yyyy', 'en-US');

            this.getStudentsListsDrop();
            this.getStudentTeams();
            this.getStudentCounselor();
        } else {
            // single student mode
            this.subscriptions.push(this.yearService.observableStudent360YearStream$.subscribe(student360Year => {
                this.defaultYear = student360Year;
                this.getCourseChangeHistoryData();
            }));
            this.scheduleHistorySearchModel.studentNumber = this.studentService.selectedStudent.StudentNumber;
            this.getCourseChangeHistoryData();
        }
    }

    getCourseChangeHistoryData() {
        this.scheduleHistoryService.getCourseChangeHistory(this.defaultYear.SchoolID, this.defaultYear.Id, this.scheduleHistorySearchModel).subscribe(result => {
            this.scheduleHistoryModel = result;
            this.angularMatData = new TylerMatTableDataSource(this.scheduleHistoryModel);
            this.angularMatData.sort = this.sort;
        });
    }

    getStudentTeams() {
        this.teamsService.getTeamList(this.yearService.defaultYear.SchoolID, this.yearService.defaultYear.Id).subscribe(result => {
            this.teams = result;
        });
    }

    getStudentCounselor() {
        this.counselorService.getStaffByRoles(0, 'Counselor').subscribe(result => {
            this.counselor = result;
        });
    }

    getStudentsListsDrop() {
        this.studentListsService.getStudentLists().subscribe(x => this.studentLists = x.sort((a, b) =>
            a.Name.localeCompare(b.Name)));
    }

    clearFilters() {
        this.scheduleHistorySearchModel = new ScheduleHistorySearchModel('');
        this.angularMatData.data = [];
    }

    enterStudentNumbers() {
        const dialogBox = this.dialog.open(EnterCsvDialogComponent, {
            disableClose: true,
            data: {
                header: 'Enter Student Numbers',
                helpString: 'Separate numbers with a comma (Ex. 12345,13423,12312,19712)',
                pattern: this.studentNumberRegExp,
                keyRestriction: this.studentNumberKeyRestriction,
                validator: (csv: string): ErrorList[] => {
                    if (!csv) {
                        return [
                            {
                                errorMsg: 'Required field.',
                                fieldName: 'Student Numbers',
                                fieldRef: new ElementRef(null)
                            }
                        ];
                    }
                    if (csv[csv.length - 1] == ',') {
                        return [
                            {
                                errorMsg: 'The CSV must not end with a comma.',
                                fieldName: 'Student Numbers',
                                fieldRef: new ElementRef(null)
                            }
                        ];
                    } else if (!csv.match(this.studentNumberRegExp)) {
                        return [
                            {
                                errorMsg: 'Separate numbers by commas. No spaces or special characters.',
                                fieldName: 'Student Numbers',
                                fieldRef: new ElementRef(null)
                            }
                        ];
                    }
                    return [];
                }
            }
        });
        this.subscriptions.push(
            dialogBox.afterClosed().subscribe(csv => {
                if (csv || csv == '') {
                    this.scheduleHistorySearchModel.studentNumber = csv;
                }
            })
        );
    }

    searchClick() {
        this.validate();
        if (this.errors.length == 0) {
            this.scheduleHistorySearchModel.classEntryDateFrom = formatDate(this.scheduleHistorySearchModel.classEntryDateFrom, 'MM/dd/yyyy', 'en-US');
            this.scheduleHistorySearchModel.classEntryDateTo = formatDate(this.scheduleHistorySearchModel.classEntryDateTo, 'MM/dd/yyyy', 'en-US');
            this.scheduleHistorySearchModel.classExitDateFrom = formatDate(this.scheduleHistorySearchModel.classExitDateFrom, 'MM/dd/yyyy', 'en-US');
            this.scheduleHistorySearchModel.classExitDateTo = formatDate(this.scheduleHistorySearchModel.classExitDateTo, 'MM/dd/yyyy', 'en-US');
            this.getCourseChangeHistoryData();
        }
    }

    validate() {
        this.errors = [];
        if (this.scheduleHistorySearchModel.classEntryDateFrom == '') {
            this.errors.push({
                fieldName: 'Class Entry Date From',
                fieldRef: this.classEntryDateFrom,
                errorMsg: ' is Required.'
            });
        }
        if (this.scheduleHistorySearchModel.classEntryDateTo == '') {
            this.errors.push({
                fieldName: 'Class Entry Date To',
                fieldRef: this.classEntryDateTo,
                errorMsg: ' is Required.'
            });
        }
        if (this.scheduleHistorySearchModel.classExitDateFrom == '') {
            this.errors.push({
                fieldName: 'Class Exit Date From',
                fieldRef: this.classExitDateFrom,
                errorMsg: ' is Required.'
            });
        }
        if (this.scheduleHistorySearchModel.classExitDateTo == '') {
            this.errors.push({
                fieldName: 'Class Exit Date To',
                fieldRef: this.classExitDateTo,
                errorMsg: ' is Required.'
            });
        }
    }

    keyDownFunction(event) {
        if (event.keyCode == 13) {
            this.searchClick();
        }
    }

    ngOnDestroy() { }
}
